// src/data/articles/types-of-humor.js
// All language versions of the "Types of Humor" article.
//
// Keyword strategy:
//   EN → "types of humor", "types of comedy", "humor styles", "kinds of humor", "what makes people laugh"
//   FR → "types d'humour", "styles d'humour", "formes d'humour", "qu'est-ce qui fait rire", "humour absurde observationnel"
//   ES → "tipos de humor", "estilos de humor", "clases de humor", "qué hace reír a la gente", "humor observacional absurdo"
//   AR → "أنواع الفكاهة", "أنواع الكوميديا", "أساليب الفكاهة", "ما يجعل الناس يضحكون", "الفكاهة العبثية الملاحظاتية"

export default [
  // ── English ───────────────────────────────────────────────────────────────
  {
    slug: 'types-of-humor',
    lang: 'en',
    translations: { en: 'types-of-humor', fr: 'types-dhumour', es: 'tipos-de-humor', ar: 'anwaa-al-fukaha' },
    title: 'Types of Humor: Understanding What Makes People Laugh',
    seoTitle: 'Types of Humor: Understanding What Makes People Laugh',
    seoDescription:
      'Explore the most common types of humor — from absurd and dark to observational and self-deprecating. Learn how different comedy styles work and find your own humor style.',
    publishedDate: '2026-03-06',
    readingMinutes: 7,
    relatedCategories: ['general', 'friends', 'school', 'work'],
    relatedArticles: ['joke-structures', 'how-to-write-a-funny-joke', 'benefits-of-laughter'],
    intro: [
      'Humor is a universal part of human life, yet not everyone laughs at the same things. What one person finds hilarious, another might find confusing or even offensive. This is because humor comes in many different forms.',
      'Understanding the types of humor can help you appreciate comedy more deeply and even improve your own ability to make people laugh. From clever wordplay to sarcastic remarks, each style of humor relies on different psychological and social mechanisms.',
      'Here are some of the most common types of humor found in jokes, comedy shows, and everyday conversations.',
    ],
    sections: [
      {
        number: 1,
        heading: 'Absurd Humor',
        anchor: 'absurd-humor',
        content: [
          { type: 'paragraph', text: 'Absurd humor is based on illogical or nonsensical situations. It breaks the rules of normal logic to create a surprising and often ridiculous effect.' },
          { type: 'lead', text: 'Example:' },
          { type: 'paragraph', text: 'A person having a serious conversation with a chair as if it could respond.' },
          { type: 'paragraph', text: "The humor comes from the brain trying to make sense of something that clearly doesn't make sense." },
          { type: 'paragraph', text: 'Absurd humor is common in surreal comedy and creative storytelling.' },
        ],
      },
      {
        number: 2,
        heading: 'Dark Humor',
        anchor: 'dark-humor',
        content: [
          { type: 'paragraph', text: 'Dark humor focuses on serious or taboo topics such as death, illness, or tragedy, but approaches them in a humorous way.' },
          { type: 'lead', text: 'This type of humor is often used to:' },
          { type: 'list', items: ['Cope with difficult situations', 'Criticize social issues', 'Challenge cultural norms'] },
          { type: 'paragraph', text: 'However, dark humor can be controversial depending on the audience and context.' },
        ],
      },
      {
        number: 3,
        heading: 'Sarcastic Humor',
        anchor: 'sarcastic-humor',
        content: [
          { type: 'paragraph', text: 'Sarcasm involves saying the opposite of what you actually mean, usually in a sharp or mocking tone.' },
          { type: 'lead', text: 'Example:' },
          { type: 'paragraph', text: 'Saying "Great idea!" after someone makes a clearly bad decision.' },
          { type: 'paragraph', text: 'Sarcastic humor is common in everyday conversations, sitcoms, and social media.' },
        ],
      },
      {
        number: 4,
        heading: 'Ironic Humor',
        anchor: 'ironic-humor',
        content: [
          { type: 'paragraph', text: 'Ironic humor is similar to sarcasm but usually more subtle. It relies on a contrast between what is said and what is actually happening.' },
          { type: 'lead', text: 'Example:' },
          { type: 'paragraph', text: 'Saying "What a beautiful day" during a heavy rainstorm.' },
          { type: 'paragraph', text: 'Irony requires the audience to recognize the contrast between words and reality.' },
        ],
      },
      {
        number: 5,
        heading: 'Situational Humor',
        anchor: 'situational-humor',
        content: [
          { type: 'paragraph', text: 'Situational humor arises from a particular context or unexpected series of events.' },
          { type: 'lead', text: 'It is commonly used in:' },
          { type: 'list', items: ['Comedy movies', 'Sitcoms', 'Stage performances', 'Storytelling jokes'] },
          { type: 'paragraph', text: 'The humor comes from how characters react to unusual or awkward situations.' },
        ],
      },
      {
        number: 6,
        heading: 'Wordplay Humor (Puns)',
        anchor: 'wordplay-humor-puns',
        content: [
          { type: 'paragraph', text: 'Wordplay humor uses language tricks such as double meanings, similar-sounding words, and playful interpretations.' },
          { type: 'lead', text: 'Example:' },
          { type: 'paragraph', text: "I used to be a baker, but I couldn't make enough dough." },
          { type: 'paragraph', text: "Puns are very popular in short jokes, riddles, and children's humor." },
        ],
      },
      {
        number: 7,
        heading: 'Observational Humor',
        anchor: 'observational-humor',
        content: [
          { type: 'paragraph', text: 'Observational humor focuses on everyday situations that people commonly experience.' },
          { type: 'lead', text: 'Comedians often talk about:' },
          { type: 'list', items: ['Family life', 'Work routines', 'Relationships', 'Technology problems', 'Daily habits'] },
          { type: 'paragraph', text: 'People laugh because they recognize themselves in the situation.' },
        ],
      },
      {
        number: 8,
        heading: 'Self-Deprecating Humor',
        anchor: 'self-deprecating-humor',
        content: [
          { type: 'paragraph', text: 'Self-deprecating humor involves making jokes about yourself.' },
          { type: 'paragraph', text: 'This type of humor can make a person appear more relatable and approachable.' },
          { type: 'paragraph', text: 'Many comedians use self-deprecating humor to connect with their audience and create a relaxed atmosphere.' },
        ],
      },
      {
        number: 9,
        heading: 'Childlike or Silly Humor',
        anchor: 'childlike-silly-humor',
        content: [
          { type: 'paragraph', text: 'Childlike humor is simple, playful, and sometimes exaggerated.' },
          { type: 'lead', text: 'It often includes:' },
          { type: 'list', items: ['Silly situations', 'Talking animals', 'Exaggerated reactions', 'Playful sounds or expressions'] },
          { type: 'paragraph', text: 'This type of humor works especially well with younger audiences.' },
        ],
      },
      {
        number: 10,
        heading: 'Why Do Different Types of Humor Exist?',
        anchor: 'why-different-types-exist',
        content: [
          { type: 'paragraph', text: 'Humor is influenced by many factors, including culture, personality, life experiences, and social environment.' },
          { type: 'paragraph', text: 'Because people have different backgrounds and perspectives, they respond differently to various humor styles.' },
          { type: 'paragraph', text: 'This diversity is what makes comedy so rich and creative.' },
        ],
      },
    ],
    tips: {
      heading: 'How to Find Your Personal Humor Style',
      intro: 'If you want to develop your sense of humor, try these steps:',
      items: [
        'Observe comedians you enjoy',
        'Experiment with different humor styles',
        'Notice what makes people laugh around you',
        'Practice telling jokes or funny stories',
      ],
      outro: 'Over time, most people naturally develop a humor style that reflects their personality.',
    },
    faq: [
      {
        q: 'What is the most common type of humor?',
        a: 'Observational humor and situational humor are among the most widely appreciated forms of comedy.',
      },
      {
        q: 'Why do people enjoy dark humor?',
        a: 'For some people, dark humor helps them cope with difficult topics or reduces emotional tension.',
      },
      {
        q: 'Can a joke combine multiple humor types?',
        a: 'Yes. Many jokes mix different styles such as sarcasm, exaggeration, and wordplay.',
      },
      {
        q: 'Does humor vary between cultures?',
        a: 'Absolutely. Cultural background strongly influences what people find funny.',
      },
    ],
    conclusion: [
      'Understanding the different types of humor reveals how complex and fascinating laughter can be. From absurd and sarcastic humor to observational and self-deprecating jokes, each style uses different techniques to create amusement.',
      'Exploring these humor styles can help you appreciate comedy more deeply and even improve your own ability to make others laugh.',
    ],
  },

  // ── French ────────────────────────────────────────────────────────────────
  // Keywords: "types d'humour", "styles d'humour", "humour absurde", "humour observationnel", "qu'est-ce qui fait rire"
  {
    slug: 'types-dhumour',
    lang: 'fr',
    translations: { en: 'types-of-humor', fr: 'types-dhumour', es: 'tipos-de-humor', ar: 'anwaa-al-fukaha' },
    title: "Types d'Humour : Comprendre ce qui Fait Rire les Gens",
    seoTitle: "Types d'Humour : Comprendre ce qui Fait Rire les Gens",
    seoDescription:
      "Explorez les types d'humour les plus courants : absurde, noir, sarcastique, ironique, observationnel et autodérision. Découvrez comment chaque style comique fonctionne et trouvez votre propre style.",
    publishedDate: '2026-03-06',
    readingMinutes: 7,
    relatedCategories: ['general', 'friends', 'school', 'work'],
    relatedArticles: ['structures-de-blague', 'comment-ecrire-une-blague', 'bienfaits-du-rire'],
    intro: [
      "L'humour est une part universelle de la vie humaine, pourtant tout le monde ne rit pas des mêmes choses. Ce qui fait éclater de rire une personne peut laisser une autre perplexe, voire choquée. C'est parce que l'humour prend de nombreuses formes différentes.",
      "Comprendre les types d'humour peut vous aider à apprécier la comédie plus profondément et même améliorer votre propre capacité à faire rire. Du jeu de mots habile aux remarques sarcastiques, chaque style d'humour repose sur des mécanismes psychologiques et sociaux différents.",
      "Voici quelques-uns des types d'humour les plus courants dans les blagues, les émissions comiques et les conversations quotidiennes.",
    ],
    sections: [
      {
        number: 1,
        heading: "L'Humour Absurde",
        anchor: 'humour-absurde',
        content: [
          { type: 'paragraph', text: "L'humour absurde repose sur des situations illogiques ou dépourvues de sens. Il brise les règles de la logique ordinaire pour créer un effet surprenant et souvent ridicule." },
          { type: 'lead', text: 'Exemple :' },
          { type: 'paragraph', text: "Une personne qui entretient une conversation sérieuse avec une chaise comme si elle pouvait répondre." },
          { type: 'paragraph', text: "Le rire naît du cerveau qui tente de donner du sens à quelque chose qui n'en a clairement pas." },
          { type: 'paragraph', text: "L'humour absurde est courant dans la comédie surréaliste et la narration créative." },
        ],
      },
      {
        number: 2,
        heading: "L'Humour Noir",
        anchor: 'humour-noir',
        content: [
          { type: 'paragraph', text: "L'humour noir aborde des sujets sérieux ou tabous tels que la mort, la maladie ou la tragédie, mais de manière humoristique." },
          { type: 'lead', text: "Ce type d'humour est souvent utilisé pour :" },
          { type: 'list', items: ['Faire face à des situations difficiles', 'Critiquer les problèmes sociaux', 'Remettre en question les normes culturelles'] },
          { type: 'paragraph', text: "Cependant, l'humour noir peut être controversé selon le public et le contexte." },
        ],
      },
      {
        number: 3,
        heading: "L'Humour Sarcastique",
        anchor: 'humour-sarcastique',
        content: [
          { type: 'paragraph', text: "Le sarcasme consiste à dire le contraire de ce qu'on pense réellement, généralement sur un ton vif ou moqueur." },
          { type: 'lead', text: 'Exemple :' },
          { type: 'paragraph', text: "Dire « Quelle bonne idée ! » après qu'une personne a pris une décision manifestement mauvaise." },
          { type: 'paragraph', text: "L'humour sarcastique est courant dans les conversations quotidiennes, les sitcoms et les réseaux sociaux." },
        ],
      },
      {
        number: 4,
        heading: "L'Humour Ironique",
        anchor: 'humour-ironique',
        content: [
          { type: 'paragraph', text: "L'humour ironique est similaire au sarcasme mais généralement plus subtil. Il repose sur un contraste entre ce qui est dit et ce qui se passe réellement." },
          { type: 'lead', text: 'Exemple :' },
          { type: 'paragraph', text: 'Dire « Quelle belle journée » sous une pluie battante.' },
          { type: 'paragraph', text: "L'ironie exige que le public reconnaisse le contraste entre les mots et la réalité." },
        ],
      },
      {
        number: 5,
        heading: "L'Humour Situationnel",
        anchor: 'humour-situationnel',
        content: [
          { type: 'paragraph', text: "L'humour situationnel naît d'un contexte particulier ou d'une série d'événements inattendus." },
          { type: 'lead', text: 'On le retrouve couramment dans :' },
          { type: 'list', items: ['Les films comiques', 'Les sitcoms', 'Les spectacles de scène', 'Les histoires drôles'] },
          { type: 'paragraph', text: "Le rire provient de la façon dont les personnages réagissent à des situations inhabituelles ou gênantes." },
        ],
      },
      {
        number: 6,
        heading: "L'Humour de Jeux de Mots (Calembours)",
        anchor: 'humour-jeux-de-mots-calembours',
        content: [
          { type: 'paragraph', text: "L'humour de jeux de mots utilise des astuces linguistiques telles que les doubles sens, les homophones et les interprétations ludiques." },
          { type: 'lead', text: 'Exemple :' },
          { type: 'paragraph', text: "J'étais boulanger, mais je ne gagnais pas assez de blé." },
          { type: 'paragraph', text: "Les calembours sont très populaires dans les blagues courtes, les devinettes et l'humour pour enfants." },
        ],
      },
      {
        number: 7,
        heading: "L'Humour Observationnel",
        anchor: 'humour-observationnel',
        content: [
          { type: 'paragraph', text: "L'humour observationnel se concentre sur les situations quotidiennes que les gens vivent communément." },
          { type: 'lead', text: 'Les comédiens parlent souvent de :' },
          { type: 'list', items: ['La vie de famille', 'Les routines de travail', 'Les relations', 'Les problèmes technologiques', 'Les habitudes quotidiennes'] },
          { type: 'paragraph', text: "Les gens rient parce qu'ils se reconnaissent dans la situation." },
        ],
      },
      {
        number: 8,
        heading: "L'Autodérision",
        anchor: 'autodérision',
        content: [
          { type: 'paragraph', text: "L'autodérision consiste à se moquer de soi-même." },
          { type: 'paragraph', text: "Ce type d'humour peut rendre une personne plus attachante et accessible." },
          { type: 'paragraph', text: "De nombreux comédiens utilisent l'autodérision pour créer un lien avec leur public et instaurer une atmosphère détendue." },
        ],
      },
      {
        number: 9,
        heading: "L'Humour Enfantin ou Loufoque",
        anchor: 'humour-enfantin-loufoque',
        content: [
          { type: 'paragraph', text: "L'humour enfantin est simple, joueur et parfois exagéré." },
          { type: 'lead', text: 'Il inclut souvent :' },
          { type: 'list', items: ['Des situations loufoques', 'Des animaux qui parlent', 'Des réactions exagérées', 'Des sons ou expressions ludiques'] },
          { type: 'paragraph', text: "Ce type d'humour fonctionne particulièrement bien avec les jeunes audiences." },
        ],
      },
      {
        number: 10,
        heading: "Pourquoi Existe-t-il Différents Types d'Humour ?",
        anchor: 'pourquoi-differents-types-humour',
        content: [
          { type: 'paragraph', text: "L'humour est influencé par de nombreux facteurs, notamment la culture, la personnalité, les expériences de vie et l'environnement social." },
          { type: 'paragraph', text: "Parce que les gens ont des parcours et des perspectives différents, ils réagissent différemment aux divers styles d'humour." },
          { type: 'paragraph', text: "Cette diversité est ce qui rend la comédie si riche et créative." },
        ],
      },
    ],
    tips: {
      heading: "Comment Trouver Votre Style d'Humour Personnel",
      intro: "Si vous souhaitez développer votre sens de l'humour, essayez ces étapes :",
      items: [
        'Observez les comédiens que vous appréciez',
        "Expérimentez différents styles d'humour",
        'Remarquez ce qui fait rire les gens autour de vous',
        'Entraînez-vous à raconter des blagues ou des histoires drôles',
      ],
      outro: "Avec le temps, la plupart des gens développent naturellement un style d'humour qui reflète leur personnalité.",
    },
    faq: [
      {
        q: "Quel est le type d'humour le plus courant ?",
        a: "L'humour observationnel et l'humour situationnel sont parmi les formes de comédie les plus appréciées.",
      },
      {
        q: "Pourquoi les gens aiment-ils l'humour noir ?",
        a: "Pour certaines personnes, l'humour noir les aide à gérer des sujets difficiles ou à réduire la tension émotionnelle.",
      },
      {
        q: "Une blague peut-elle combiner plusieurs types d'humour ?",
        a: "Oui. Beaucoup de blagues mélangent différents styles comme le sarcasme, l'exagération et le jeu de mots.",
      },
      {
        q: "L'humour varie-t-il selon les cultures ?",
        a: "Absolument. Le contexte culturel influence fortement ce que les gens trouvent drôle.",
      },
    ],
    conclusion: [
      "Comprendre les différents types d'humour révèle à quel point le rire peut être complexe et fascinant. De l'humour absurde et sarcastique aux blagues observationnelles et autodérisoires, chaque style utilise des techniques différentes pour créer de l'amusement.",
      "Explorer ces styles d'humour peut vous aider à apprécier la comédie plus profondément et même améliorer votre propre capacité à faire rire les autres.",
    ],
  },

  // ── Spanish ───────────────────────────────────────────────────────────────
  // Keywords: "tipos de humor", "estilos de humor", "humor absurdo", "humor observacional", "qué hace reír a la gente"
  {
    slug: 'tipos-de-humor',
    lang: 'es',
    translations: { en: 'types-of-humor', fr: 'types-dhumour', es: 'tipos-de-humor', ar: 'anwaa-al-fukaha' },
    title: 'Tipos de Humor: Entendiendo qué Hace Reír a la Gente',
    seoTitle: 'Tipos de Humor: Entendiendo qué Hace Reír a la Gente',
    seoDescription:
      'Explora los tipos de humor más comunes: absurdo, negro, sarcástico, irónico, observacional y autodespreciativo. Aprende cómo funciona cada estilo cómico y encuentra tu propio estilo.',
    publishedDate: '2026-03-06',
    readingMinutes: 7,
    relatedCategories: ['general', 'friends', 'school', 'work'],
    relatedArticles: ['estructuras-de-chiste', 'como-escribir-un-chiste', 'beneficios-de-la-risa'],
    intro: [
      'El humor es una parte universal de la vida humana, pero no todo el mundo se ríe de las mismas cosas. Lo que una persona encuentra hilarante, otra puede encontrarlo confuso o incluso ofensivo. Esto se debe a que el humor adopta muchas formas diferentes.',
      'Entender los tipos de humor puede ayudarte a apreciar la comedia más profundamente e incluso mejorar tu propia capacidad para hacer reír. Desde el juego de palabras inteligente hasta los comentarios sarcásticos, cada estilo de humor se basa en diferentes mecanismos psicológicos y sociales.',
      'Aquí están algunos de los tipos de humor más comunes que se encuentran en los chistes, los programas de comedia y las conversaciones cotidianas.',
    ],
    sections: [
      {
        number: 1,
        heading: 'Humor Absurdo',
        anchor: 'humor-absurdo',
        content: [
          { type: 'paragraph', text: 'El humor absurdo se basa en situaciones ilógicas o sin sentido. Rompe las reglas de la lógica normal para crear un efecto sorprendente y a menudo ridículo.' },
          { type: 'lead', text: 'Ejemplo:' },
          { type: 'paragraph', text: 'Una persona manteniendo una conversación seria con una silla como si pudiera responder.' },
          { type: 'paragraph', text: 'El humor proviene del cerebro intentando dar sentido a algo que claramente no lo tiene.' },
          { type: 'paragraph', text: 'El humor absurdo es común en la comedia surrealista y la narración creativa.' },
        ],
      },
      {
        number: 2,
        heading: 'Humor Negro',
        anchor: 'humor-negro',
        content: [
          { type: 'paragraph', text: 'El humor negro se centra en temas serios o tabúes como la muerte, la enfermedad o la tragedia, pero los aborda de manera humorística.' },
          { type: 'lead', text: 'Este tipo de humor se usa a menudo para:' },
          { type: 'list', items: ['Hacer frente a situaciones difíciles', 'Criticar problemas sociales', 'Desafiar las normas culturales'] },
          { type: 'paragraph', text: 'Sin embargo, el humor negro puede ser controvertido dependiendo del público y el contexto.' },
        ],
      },
      {
        number: 3,
        heading: 'Humor Sarcástico',
        anchor: 'humor-sarcastico',
        content: [
          { type: 'paragraph', text: 'El sarcasmo implica decir lo contrario de lo que realmente se quiere decir, generalmente con un tono agudo o burlón.' },
          { type: 'lead', text: 'Ejemplo:' },
          { type: 'paragraph', text: 'Decir "¡Qué gran idea!" después de que alguien tome una decisión claramente mala.' },
          { type: 'paragraph', text: 'El humor sarcástico es común en las conversaciones cotidianas, las sitcoms y las redes sociales.' },
        ],
      },
      {
        number: 4,
        heading: 'Humor Irónico',
        anchor: 'humor-ironico',
        content: [
          { type: 'paragraph', text: 'El humor irónico es similar al sarcasmo pero generalmente más sutil. Se basa en un contraste entre lo que se dice y lo que realmente está sucediendo.' },
          { type: 'lead', text: 'Ejemplo:' },
          { type: 'paragraph', text: 'Decir "¡Qué día tan hermoso!" durante una fuerte tormenta de lluvia.' },
          { type: 'paragraph', text: 'La ironía requiere que el público reconozca el contraste entre las palabras y la realidad.' },
        ],
      },
      {
        number: 5,
        heading: 'Humor Situacional',
        anchor: 'humor-situacional',
        content: [
          { type: 'paragraph', text: 'El humor situacional surge de un contexto particular o una serie inesperada de eventos.' },
          { type: 'lead', text: 'Se usa comúnmente en:' },
          { type: 'list', items: ['Películas de comedia', 'Sitcoms', 'Actuaciones en el escenario', 'Chistes narrativos'] },
          { type: 'paragraph', text: 'El humor proviene de cómo los personajes reaccionan ante situaciones inusuales o incómodas.' },
        ],
      },
      {
        number: 6,
        heading: 'Humor de Juego de Palabras (Calambures)',
        anchor: 'humor-juego-de-palabras-calambures',
        content: [
          { type: 'paragraph', text: 'El humor de juego de palabras utiliza trucos lingüísticos como dobles sentidos, palabras que suenan parecido e interpretaciones lúdicas.' },
          { type: 'lead', text: 'Ejemplo:' },
          { type: 'paragraph', text: 'Era panadero, pero no me levantaba el pan.' },
          { type: 'paragraph', text: 'Los calambures son muy populares en los chistes cortos, los acertijos y el humor infantil.' },
        ],
      },
      {
        number: 7,
        heading: 'Humor Observacional',
        anchor: 'humor-observacional',
        content: [
          { type: 'paragraph', text: 'El humor observacional se centra en situaciones cotidianas que la gente experimenta comúnmente.' },
          { type: 'lead', text: 'Los comediantes hablan a menudo de:' },
          { type: 'list', items: ['La vida familiar', 'Las rutinas de trabajo', 'Las relaciones', 'Los problemas tecnológicos', 'Los hábitos diarios'] },
          { type: 'paragraph', text: 'La gente ríe porque se reconoce a sí misma en la situación.' },
        ],
      },
      {
        number: 8,
        heading: 'Humor Autodespreciativo',
        anchor: 'humor-autodespreciativo',
        content: [
          { type: 'paragraph', text: 'El humor autodespreciativo implica hacer chistes sobre uno mismo.' },
          { type: 'paragraph', text: 'Este tipo de humor puede hacer que una persona parezca más cercana y accesible.' },
          { type: 'paragraph', text: 'Muchos comediantes usan el humor autodespreciativo para conectar con su público y crear un ambiente relajado.' },
        ],
      },
      {
        number: 9,
        heading: 'Humor Infantil o Payasesco',
        anchor: 'humor-infantil-payasesco',
        content: [
          { type: 'paragraph', text: 'El humor infantil es simple, juguetón y a veces exagerado.' },
          { type: 'lead', text: 'A menudo incluye:' },
          { type: 'list', items: ['Situaciones ridículas', 'Animales que hablan', 'Reacciones exageradas', 'Sonidos o expresiones juguetones'] },
          { type: 'paragraph', text: 'Este tipo de humor funciona especialmente bien con audiencias más jóvenes.' },
        ],
      },
      {
        number: 10,
        heading: '¿Por qué Existen Diferentes Tipos de Humor?',
        anchor: 'por-que-diferentes-tipos-humor',
        content: [
          { type: 'paragraph', text: 'El humor está influenciado por muchos factores, incluyendo la cultura, la personalidad, las experiencias de vida y el entorno social.' },
          { type: 'paragraph', text: 'Debido a que las personas tienen diferentes contextos y perspectivas, responden de manera diferente a los distintos estilos de humor.' },
          { type: 'paragraph', text: 'Esta diversidad es lo que hace que la comedia sea tan rica y creativa.' },
        ],
      },
    ],
    tips: {
      heading: 'Cómo Encontrar Tu Estilo de Humor Personal',
      intro: 'Si quieres desarrollar tu sentido del humor, prueba estos pasos:',
      items: [
        'Observa a los comediantes que disfrutas',
        'Experimenta con diferentes estilos de humor',
        'Fíjate en qué hace reír a las personas a tu alrededor',
        'Practica contando chistes o historias divertidas',
      ],
      outro: 'Con el tiempo, la mayoría de las personas desarrolla naturalmente un estilo de humor que refleja su personalidad.',
    },
    faq: [
      {
        q: '¿Cuál es el tipo de humor más común?',
        a: 'El humor observacional y el humor situacional son de las formas de comedia más apreciadas.',
      },
      {
        q: '¿Por qué a la gente le gusta el humor negro?',
        a: 'Para algunas personas, el humor negro les ayuda a afrontar temas difíciles o reduce la tensión emocional.',
      },
      {
        q: '¿Puede un chiste combinar múltiples tipos de humor?',
        a: 'Sí. Muchos chistes mezclan diferentes estilos como el sarcasmo, la exageración y el juego de palabras.',
      },
      {
        q: '¿El humor varía entre culturas?',
        a: 'Absolutamente. El contexto cultural influye fuertemente en lo que la gente encuentra gracioso.',
      },
    ],
    conclusion: [
      'Entender los diferentes tipos de humor revela lo compleja y fascinante que puede ser la risa. Desde el humor absurdo y sarcástico hasta los chistes observacionales y autodespreciativos, cada estilo usa diferentes técnicas para crear diversión.',
      'Explorar estos estilos de humor puede ayudarte a apreciar la comedia más profundamente e incluso mejorar tu propia capacidad para hacer reír a los demás.',
    ],
  },

  // ── Arabic ────────────────────────────────────────────────────────────────
  // Keywords: "أنواع الفكاهة", "أنواع الكوميديا", "أساليب الفكاهة", "ما يجعل الناس يضحكون", "الفكاهة الملاحظاتية"
  {
    slug: 'anwaa-al-fukaha',
    lang: 'ar',
    translations: { en: 'types-of-humor', fr: 'types-dhumour', es: 'tipos-de-humor', ar: 'anwaa-al-fukaha' },
    title: 'أنواع الفكاهة: فهم ما يجعل الناس يضحكون',
    seoTitle: 'أنواع الفكاهة: فهم ما يجعل الناس يضحكون',
    seoDescription:
      'اكتشف أكثر أنواع الفكاهة شيوعاً: العبثية والسوداء والساخرة والموقفية والملاحظاتية والاستهزائية بالنفس. تعرّف على كيفية عمل كل أسلوب كوميدي واكتشف أسلوبك الفكاهي الشخصي.',
    publishedDate: '2026-03-06',
    readingMinutes: 7,
    relatedCategories: ['general', 'friends', 'school', 'work'],
    relatedArticles: ['haykal-an-nukta', 'kaifa-taktub-nuktatan', 'fawaid-al-dahk'],
    intro: [
      'الفكاهة جزء عالمي من الحياة الإنسانية، غير أن الناس لا يضحكون من الأشياء ذاتها. ما يُضحك شخصاً قد يجد فيه آخر حيرةً أو حتى إساءة. والسبب في ذلك أن الفكاهة تتخذ أشكالاً متعددة ومتنوعة.',
      'فهم أنواع الفكاهة يُتيح لك تذوّق الكوميديا بعمق أكبر، بل وتحسين قدرتك الشخصية على إضحاك الآخرين. من الألعاب اللفظية الذكية إلى التعليقات الساخرة، يعتمد كل أسلوب فكاهي على آليات نفسية واجتماعية مختلفة.',
      'فيما يلي أبرز أنواع الفكاهة الشائعة في النكات وبرامج الكوميديا والمحادثات اليومية.',
    ],
    sections: [
      {
        number: 1,
        heading: 'الفكاهة العبثية',
        anchor: 'al-fukaha-al-absurdia',
        content: [
          { type: 'paragraph', text: 'تقوم الفكاهة العبثية على مواقف غير منطقية ومجردة من المعنى. تكسر قواعد المنطق العادي لتصنع أثراً مفاجئاً وسخيفاً في آنٍ واحد.' },
          { type: 'lead', text: 'مثال:' },
          { type: 'paragraph', text: 'شخص يُجري محادثة جدية مع كرسي كأنه قادر على الردّ.' },
          { type: 'paragraph', text: 'يكمن الضحك في محاولة الدماغ إيجاد معنى لشيء لا معنى له على الإطلاق.' },
          { type: 'paragraph', text: 'الفكاهة العبثية شائعة في الكوميديا السريالية والقصص الإبداعية المتخيَّلة.' },
        ],
      },
      {
        number: 2,
        heading: 'الفكاهة السوداء',
        anchor: 'al-fukaha-as-sawdaa',
        content: [
          { type: 'paragraph', text: 'تتناول الفكاهة السوداء موضوعات جادة أو مثيرة للجدل كالموت والمرض والمأساة، لكنها تُعالجها بأسلوب فكاهي.' },
          { type: 'lead', text: 'كثيراً ما يُستعان بهذا النوع من الفكاهة من أجل:' },
          { type: 'list', items: ['التأقلم مع المواقف الصعبة', 'انتقاد المشكلات الاجتماعية', 'تحدّي الأعراف الثقافية السائدة'] },
          { type: 'paragraph', text: 'غير أن الفكاهة السوداء قد تثير الجدل بحسب طبيعة الجمهور والسياق.' },
        ],
      },
      {
        number: 3,
        heading: 'الفكاهة الساخرة',
        anchor: 'al-fukaha-as-sakhira',
        content: [
          { type: 'paragraph', text: 'تقوم السخرية على قول عكس ما يُقصد فعلاً، عادةً بنبرة حادة أو استهزائية.' },
          { type: 'lead', text: 'مثال:' },
          { type: 'paragraph', text: 'قول "يا له من قرار رائع!" إثر اتخاذ شخص ما قراراً سيئاً بوضوح.' },
          { type: 'paragraph', text: 'الفكاهة الساخرة شائعة في المحادثات اليومية والمسلسلات الكوميدية ومنصات التواصل الاجتماعي.' },
        ],
      },
      {
        number: 4,
        heading: 'الفكاهة الإيرونية',
        anchor: 'al-fukaha-al-irawniya',
        content: [
          { type: 'paragraph', text: 'تشبه الفكاهة الإيرونية السخرية لكنها في الغالب أكثر خفاءً ودقة. تعتمد على التناقض بين ما يُقال وما يحدث فعلاً.' },
          { type: 'lead', text: 'مثال:' },
          { type: 'paragraph', text: 'قول "يا له من يوم جميل!" وسط عاصفة مطرية شديدة.' },
          { type: 'paragraph', text: 'تستوجب الإيرونيا أن يُدرك الجمهور التناقض القائم بين الكلمات والواقع.' },
        ],
      },
      {
        number: 5,
        heading: 'الفكاهة الموقفية',
        anchor: 'al-fukaha-al-mawqifiya',
        content: [
          { type: 'paragraph', text: 'تنشأ الفكاهة الموقفية من سياق بعينه أو من تسلسل غير متوقع من الأحداث.' },
          { type: 'lead', text: 'وتُستخدم على نطاق واسع في:' },
          { type: 'list', items: ['الأفلام الكوميدية', 'المسلسلات الكوميدية (السيتكوم)', 'العروض المسرحية', 'النكات القصصية'] },
          { type: 'paragraph', text: 'يتولّد الضحك من أسلوب تعامل الشخصيات مع المواقف الغريبة أو المحرجة.' },
        ],
      },
      {
        number: 6,
        heading: 'فكاهة الألعاب اللفظية (التورية والجناس)',
        anchor: 'al-alaaab-al-lafziya',
        content: [
          { type: 'paragraph', text: 'تستعين فكاهة الألعاب اللفظية بخدع لغوية كالمعاني المزدوجة والكلمات المتشابهة في النطق والتفسيرات الخيالية.' },
          { type: 'lead', text: 'مثال:' },
          { type: 'paragraph', text: 'كنتُ أعمل خبّازاً، لكنني لم أكن أجني ما يكفي من العجين.' },
          { type: 'paragraph', text: 'التورية والجناس شائعان جداً في النكات القصيرة والألغاز وفكاهة الأطفال.' },
        ],
      },
      {
        number: 7,
        heading: 'الفكاهة الملاحظاتية',
        anchor: 'al-fukaha-al-mulahazatiya',
        content: [
          { type: 'paragraph', text: 'تُركّز الفكاهة الملاحظاتية على المواقف اليومية التي يشترك الناس في عيشها.' },
          { type: 'lead', text: 'كثيراً ما يتناول الكوميديون:' },
          { type: 'list', items: ['الحياة الأسرية', 'روتين العمل', 'العلاقات الإنسانية', 'مشكلات التكنولوجيا', 'العادات اليومية'] },
          { type: 'paragraph', text: 'يضحك الناس لأنهم يرون أنفسهم في الموقف المُصوَّر.' },
        ],
      },
      {
        number: 8,
        heading: 'الاستهزاء بالنفس',
        anchor: 'al-istihzaa-bin-nafs',
        content: [
          { type: 'paragraph', text: 'يقوم الاستهزاء بالنفس على صنع النكات على حساب شخصية صاحبها.' },
          { type: 'paragraph', text: 'يجعل هذا الأسلوب صاحبه يبدو أكثر قابليةً للتواصل وأكثر ألفةً في نظر الآخرين.' },
          { type: 'paragraph', text: 'كثير من الكوميديين يلجؤون إليه ليبنوا جسراً مع جمهورهم ويُوجدوا أجواءً مريحةً خاليةً من التوتر.' },
        ],
      },
      {
        number: 9,
        heading: 'الفكاهة الطفولية أو المرحة',
        anchor: 'al-fukaha-at-tufuliya-al-mariha',
        content: [
          { type: 'paragraph', text: 'الفكاهة الطفولية بسيطة ومرحة ومبالغ فيها أحياناً.' },
          { type: 'lead', text: 'كثيراً ما تشمل:' },
          { type: 'list', items: ['المواقف السخيفة المضحكة', 'الحيوانات الناطقة', 'ردود الفعل المبالغ فيها', 'الأصوات والتعابير المرحة'] },
          { type: 'paragraph', text: 'يُلاقي هذا النوع نجاحاً خاصاً مع الجماهير الأصغر سناً.' },
        ],
      },
      {
        number: 10,
        heading: 'لماذا توجد أنواع مختلفة من الفكاهة؟',
        anchor: 'limadha-anwaa-mukhtalifa',
        content: [
          { type: 'paragraph', text: 'تتأثر الفكاهة بعوامل كثيرة منها: الثقافة، والشخصية، والتجارب الحياتية، والبيئة الاجتماعية.' },
          { type: 'paragraph', text: 'لأن الناس يختلفون في خلفياتهم ووجهات نظرهم، يتفاوتون في استجاباتهم للأساليب الفكاهية المختلفة.' },
          { type: 'paragraph', text: 'هذا التنوع هو ما يجعل الكوميديا غنيةً بالإبداع ومتجددة لا تنضب.' },
        ],
      },
    ],
    tips: {
      heading: 'كيف تكتشف أسلوبك الفكاهي الشخصي',
      intro: 'إن أردتَ تطوير حسّك الفكاهي، جرّب هذه الخطوات:',
      items: [
        'راقب الكوميديين الذين يُعجبونك',
        'جرّب أساليب فكاهية مختلفة',
        'انتبه إلى ما يُضحك من حولك',
        'تدرّب على رواية النكات والقصص الطريفة',
      ],
      outro: 'مع الوقت، يُطوّر معظم الناس أسلوباً فكاهياً يعكس شخصيتهم بشكل طبيعي.',
    },
    faq: [
      {
        q: 'ما أكثر أنواع الفكاهة شيوعاً؟',
        a: 'الفكاهة الملاحظاتية والفكاهة الموقفية من أكثر الأشكال الكوميدية تقديراً لدى الجماهير.',
      },
      {
        q: 'لماذا يستمتع الناس بالفكاهة السوداء؟',
        a: 'تساعد الفكاهة السوداء بعض الناس على التعامل مع الموضوعات الصعبة أو التنفيس عن التوتر العاطفي.',
      },
      {
        q: 'هل يمكن للنكتة أن تجمع أنواعاً متعددة من الفكاهة؟',
        a: 'نعم. كثير من النكات تمزج بين أساليب مختلفة كالسخرية والمبالغة والألعاب اللفظية.',
      },
      {
        q: 'هل تتباين الفكاهة بين الثقافات؟',
        a: 'بالتأكيد. تؤثر الخلفية الثقافية تأثيراً بالغاً على ما يجد الناس فيه متعةً وضحكاً.',
      },
    ],
    conclusion: [
      'يكشف فهم الأنواع المختلفة للفكاهة عن مدى تعقيد الضحك وروعته. من الفكاهة العبثية والساخرة إلى النكات الملاحظاتية والاستهزائية بالنفس، يستخدم كل أسلوب تقنيات مختلفة لصنع البهجة.',
      'استكشاف هذه الأساليب يُتيح لك تذوّق الكوميديا بعمق أكبر، ويُحسّن قدرتك على إضحاك الآخرين.',
    ],
  },
];
